import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { stripeApi } from '../services/api';
import { CheckCircle, Loader2, AlertCircle, Plane, RefreshCw } from 'lucide-react';

const PaymentSuccessPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { refreshUser, user } = useAuth();
  // States: checking, processing, activating, success, error
  const [status, setStatus] = useState('checking');
  const [error, setError] = useState('');
  const [pollAttempts, setPollAttempts] = useState(0);
  const [paymentConfirmed, setPaymentConfirmed] = useState(false);
  const maxPollAttempts = 15; // More attempts since webhook may take time
  const pollInterval = 2000; // 2 seconds between checks

  const sessionId = searchParams.get('session_id');

  // Check if user has product access (granted by webhook)
  const checkUserAccess = useCallback(async () => {
    try {
      await refreshUser();
      // After refresh, check if user now has 'core' product
      // The useEffect below will handle the status update
      return true;
    } catch (err) {
      console.error('Failed to refresh user:', err);
      return false;
    }
  }, [refreshUser]);

  // Effect to check user products after refresh
  useEffect(() => {
    if (user?.products?.includes('core')) {
      setStatus('success');
    }
  }, [user?.products]);

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus();
    } else {
      setStatus('error');
      setError('No session ID provided');
    }
  }, [sessionId]);

  // Manual refresh handler for activating state
  const handleManualRefresh = async () => {
    setStatus('processing');
    await checkUserAccess();
    // If still no access after refresh, go back to activating state
    if (!user?.products?.includes('core')) {
      setStatus('activating');
    }
  };

  const pollPaymentStatus = async () => {
    // If payment was confirmed but max attempts reached waiting for webhook
    if (pollAttempts >= maxPollAttempts) {
      if (paymentConfirmed) {
        // Payment received but webhook not yet processed - show friendly activating state
        setStatus('activating');
      } else {
        // Payment status couldn't be confirmed at all
        setStatus('error');
        setError('Unable to verify payment status. Please check your email for confirmation.');
      }
      return;
    }

    try {
      // Step 1: Check payment status from Stripe
      const { data } = await stripeApi.getCheckoutStatus(sessionId);
      
      if (data.payment_status === 'paid') {
        // Payment confirmed - mark it and wait for webhook to grant access
        setPaymentConfirmed(true);
        setStatus('processing');
        
        // Step 2: Check if user has product access (webhook may have processed)
        await checkUserAccess();
        
        // If user now has access, useEffect will set status to 'success'
        // If not, keep polling
        if (!user?.products?.includes('core')) {
          setPollAttempts(prev => prev + 1);
          setTimeout(pollPaymentStatus, pollInterval);
        }
      } else if (data.status === 'expired') {
        setStatus('error');
        setError('Payment session expired. Please try again.');
      } else {
        // Still processing payment
        setPollAttempts(prev => prev + 1);
        setTimeout(pollPaymentStatus, pollInterval);
      }
    } catch (err) {
      console.error('Payment status check failed:', err);
      // On 404, the session might not be found yet, retry
      if (err.response?.status === 404 && pollAttempts < maxPollAttempts) {
        setPollAttempts(prev => prev + 1);
        setTimeout(pollPaymentStatus, pollInterval);
      } else {
        setStatus('error');
        setError('Failed to verify payment. Please check your email for confirmation.');
      }
    }
  };

  return (
    <div className="min-h-screen bg-cockpit-bg flex items-center justify-center p-4" data-testid="payment-success-page">
      <div className="max-w-md w-full bg-cockpit-panel border border-cockpit-border rounded-sm p-8 text-center">
        {status === 'checking' && (
          <>
            <div className="w-20 h-20 bg-airbus-blue/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <Loader2 className="w-10 h-10 text-airbus-blue animate-spin" />
            </div>
            <h1 className="text-2xl font-semibold text-cockpit-text-primary mb-2">
              Verifying Payment
            </h1>
            <p className="text-cockpit-text-secondary">
              Please wait while we confirm your payment...
            </p>
          </>
        )}

        {status === 'processing' && (
          <>
            <div className="w-20 h-20 bg-status-success/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <Loader2 className="w-10 h-10 text-status-success animate-spin" />
            </div>
            <h1 className="text-2xl font-semibold text-cockpit-text-primary mb-2">
              Payment Received!
            </h1>
            <p className="text-cockpit-text-secondary mb-4">
              Activating your account...
            </p>
            <p className="text-xs text-cockpit-text-secondary/70">
              This usually takes just a few seconds.
            </p>
          </>
        )}

        {status === 'activating' && (
          <>
            <div className="w-20 h-20 bg-status-success/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-status-success" />
            </div>
            <h1 className="text-2xl font-semibold text-cockpit-text-primary mb-2">
              Payment Received
            </h1>
            <p className="text-cockpit-text-secondary mb-6">
              Access will activate shortly.
            </p>
            
            <div className="bg-cockpit-bg border border-cockpit-border rounded-sm p-4 mb-6">
              <div className="flex items-center gap-3">
                <Plane className="w-6 h-6 text-airbus-blue" />
                <div className="text-left">
                  <p className="text-sm font-medium text-cockpit-text-primary">Flight Sim Academy – Fenix A320 Edition</p>
                  <p className="text-xs text-cockpit-text-secondary">Lifetime access activating...</p>
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <button
                onClick={handleManualRefresh}
                className="w-full btn-primary py-3 flex items-center justify-center gap-2"
                data-testid="refresh-access-btn"
              >
                <RefreshCw className="w-4 h-4" />
                Check Access
              </button>
              <button
                onClick={() => navigate('/dashboard')}
                className="w-full btn-secondary py-3"
              >
                Go to Dashboard
              </button>
            </div>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-20 h-20 bg-status-success/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-status-success" />
            </div>
            <h1 className="text-2xl font-semibold text-cockpit-text-primary mb-2">
              Payment Successful!
            </h1>
            <p className="text-cockpit-text-secondary mb-6">
              Your account has been activated. You now have full access to all flight phases.
            </p>
            
            <div className="bg-cockpit-bg border border-cockpit-border rounded-sm p-4 mb-6">
              <div className="flex items-center gap-3">
                <Plane className="w-6 h-6 text-airbus-blue" />
                <div className="text-left">
                  <p className="text-sm font-medium text-cockpit-text-primary">Flight Sim Academy – Fenix A320 Edition</p>
                  <p className="text-xs text-cockpit-text-secondary">Lifetime access activated</p>
                </div>
              </div>
            </div>

            <button
              onClick={() => navigate('/dashboard')}
              className="w-full btn-primary py-3"
              data-testid="go-to-dashboard-btn"
            >
              Start Exploring
            </button>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="w-20 h-20 bg-red-900/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <AlertCircle className="w-10 h-10 text-red-400" />
            </div>
            <h1 className="text-2xl font-semibold text-cockpit-text-primary mb-2">
              Verification Issue
            </h1>
            <p className="text-cockpit-text-secondary mb-6">
              {error}
            </p>
            
            <div className="space-y-3">
              <button
                onClick={() => navigate('/dashboard')}
                className="w-full btn-primary py-3"
              >
                Go to Dashboard
              </button>
              <button
                onClick={() => window.location.reload()}
                className="w-full btn-secondary py-3"
              >
                Try Again
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccessPage;
